import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Snackbar, Stack, TextField, Typography } from '@mui/material';
import Layout from '../components/Layout';

function useQueryId() {
  const parts = window.location.pathname.split('/').filter(Boolean);
  // supports /applications/add and /applications/:id/edit
  const idx = parts.indexOf('applications');
  if (idx >= 0 && parts[idx + 1] && parts[idx + 2] === 'edit') {
    return parts[idx + 1];
  }
  return null;
}

export default function ApplicationCodeEntry() {
  const editId = useQueryId();
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    vehicleNumber: '',
    mobileNumber: '',
    status: 'active',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await fetch('/api/application-codes', {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to load');
        const item = Array.isArray(data?.items) ? data.items.find((x) => x.id === editId) : null;
        if (item) {
          setForm({
            code: item.code || '',
            name: item.name || '',
            description: item.description || '',
            vehicleNumber: item.vehicleNumber || '',
            mobileNumber: item.mobileNumber || '',
            status: item.status || 'active',
          });
        }
      } catch (e) {
        setToast({ open: true, message: e?.message || 'Failed to load item', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [editId]);

  const validate = () => {
    const next = {};
    if (!form.code.trim()) next.code = 'Required';
    if (!form.name.trim()) next.name = 'Required';
    if (!form.description.trim()) next.description = 'Required';
    const st = form.status.trim().toLowerCase();
    if (!['active', 'inactive'].includes(st)) next.status = 'Use active or inactive';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim(),
        vehicleNumber: form.vehicleNumber.trim() || undefined,
        mobileNumber: form.mobileNumber.trim() || undefined,
        status: form.status.trim().toLowerCase(),
      };
      if (editId) {
        const res = await fetch(`/api/application-codes/${editId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Update failed');
        setToast({ open: true, message: 'Updated successfully', severity: 'success' });
      } else {
        const res = await fetch('/api/application-codes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Create failed');
        setToast({ open: true, message: 'Created successfully', severity: 'success' });
      }
      setTimeout(() => {
        window.location.href = '/applications';
      }, 600);
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Action failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const Header = (
    <Box
      sx={{
        borderRadius: 3,
        px: 3,
        py: 2.25,
        background: 'linear-gradient(90deg,#8b5cf6,#7c3aed)',
        color: 'white',
        mb: 2.5,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 800 }}>
        Application Code Entry
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.9 }}>
        Capture the five primary attributes and push them straight to the database.
      </Typography>
    </Box>
  );

  return (
    <Layout title="Application Code Entry">
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {Header}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: { xs: 2, md: 3 },
            borderColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'),
          }}
        >
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Application Code *"
                placeholder="e.g. APP-1092"
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                error={Boolean(errors.code)}
                helperText={errors.code ? errors.code : 'Unique identifier shared with the operations team.'}
                fullWidth
              />
              <TextField
                label="Application Name *"
                placeholder="Enter application or owner name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                error={Boolean(errors.name)}
                helperText={errors.name ? errors.name : 'This label will appear in downstream analytics.'}
                fullWidth
              />
            </Stack>
            <TextField
              label="Description *"
              placeholder="Short purpose statement or notes"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              error={Boolean(errors.description)}
              helperText={errors.description ? errors.description : 'Keep it concise — ideally under 200 characters.'}
              fullWidth
              multiline
              minRows={2}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Vehicle Number"
                placeholder="HP12AB1234"
                value={form.vehicleNumber}
                onChange={(e) => setForm((p) => ({ ...p, vehicleNumber: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Mobile Number"
                placeholder="9876543210"
                value={form.mobileNumber}
                onChange={(e) => setForm((p) => ({ ...p, mobileNumber: e.target.value }))}
                fullWidth
              />
            </Stack>
            <TextField
              label="Status"
              placeholder="active"
              value={form.status}
              onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
              error={Boolean(errors.status)}
              helperText={errors.status ? errors.status : 'Control whether the application code is currently usable.'}
              fullWidth
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                {editId ? 'Update' : 'Save'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
